import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { ContainerComponent } from '../../layout/container/container.component';
import { DiscountsService, BenefitItem } from '../../../services/discounts.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-exclusive-benefits',
  standalone: true,
  imports: [CommonModule, RouterModule, HttpClientModule, ContainerComponent],
  templateUrl: './exclusive-benefits.component.html',
  styleUrl: './exclusive-benefits.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExclusiveBenefitsComponent implements OnInit, OnDestroy, AfterViewInit {
  // Datos para cada fila
  fila1Items: BenefitItem[] = [];
  fila2Items: BenefitItem[] = [];
  fila3Items: BenefitItem[] = [];

  // Estados de carga para cada fila
  isLoadingFila1 = true;
  isLoadingFila2 = true;
  isLoadingFila3 = true;

  // Estados de error para cada fila
  errorFila1 = false;
  errorFila2 = false;
  errorFila3 = false;

  hoveredCard: number | null = null;
  private isInitialized = false;
  public deviceType: 'xl' | 'desktop' | 'tablet' | 'movil';
  private resizeTimeout: any;

  private destroy$ = new Subject<void>();
  private hoverSubject$ = new Subject<number | null>();

  @ViewChild('sectionElement', { static: false }) sectionElement?: ElementRef;
  @ViewChild('fila1Element', { static: false }) fila1Element?: ElementRef;
  @ViewChild('fila2Element', { static: false }) fila2Element?: ElementRef;
  @ViewChild('fila3Element', { static: false }) fila3Element?: ElementRef;

  constructor(
    private discountsService: DiscountsService,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    // Detectar tipo de dispositivo inicial
    this.deviceType = this.discountsService.getCurrentDeviceType();

    // Configurar debounce para eventos de hover
    this.hoverSubject$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(50), // 50ms de debounce
        distinctUntilChanged()
      )
      .subscribe(cardNumber => {
        this.hoveredCard = cardNumber;
        this.cdr.markForCheck();
      });
  }

  ngOnInit() {
    if (!this.isInitialized) {
      this.loadAllFilas();
      this.isInitialized = true;
    }
  }

  ngAfterViewInit() {
    // Registrar la sección principal para tracking de analytics
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.registerSectionForTracking(
        this.sectionElement.nativeElement,
        'Exclusive Benefits',
        'exclusive-benefits-section'
      );
    }

    // Configurar tracking de vistas para cada fila después de que se rendericen
    setTimeout(() => {
      this.setupRowViewTracking();
    }, 500);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.hoverSubject$.complete();

    // Limpiar timeout de resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Unregister section from analytics tracking
    if (this.sectionElement?.nativeElement) {
      this.analyticsService.unregisterSectionFromTracking(this.sectionElement.nativeElement);
    }
  }

  // Listener para cambios de tamaño de ventana
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Debounce para evitar múltiples llamadas durante el resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      // Obtener el nuevo tipo de dispositivo
      const newDeviceType = this.discountsService.getCurrentDeviceType();

      // Solo recargar si cambió el tipo de dispositivo
      if (newDeviceType !== this.deviceType) {
        console.log(`[ExclusiveBenefits] Dispositivo cambiado de ${this.deviceType} a ${newDeviceType}`);
        this.deviceType = newDeviceType;
        this.loadAllFilas();
      }
    }, 300); // Esperar 300ms después del último resize
  }

  private loadAllFilas() {
    console.log('🔄 Exclusive Benefits: Iniciando carga de todas las filas');
    console.log('📱 Tipo de dispositivo detectado:', this.deviceType);

    // Cargar Fila 1
    this.loadFila1(this.deviceType);

    // Cargar Fila 2
    this.loadFila2(this.deviceType);

    // Cargar Fila 3
    this.loadFila3(this.deviceType);
  }

  private loadFila1(deviceType: 'xl' | 'desktop' | 'tablet' | 'movil') {
    console.log('🔄 Exclusive Benefits: Cargando Fila 1');
    this.isLoadingFila1 = true;
    this.errorFila1 = false;
    this.cdr.markForCheck();

    this.discountsService.getBenefitsByRowAndDevice('fila1', deviceType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: BenefitItem[]) => {
          console.log('✅ Exclusive Benefits: Fila 1 cargada exitosamente', items.length);
          this.fila1Items = items.sort((a: BenefitItem, b: BenefitItem) => a.orden - b.orden);
          this.isLoadingFila1 = false;
          this.cdr.markForCheck();

          // Los eventos de vista se manejarán en setupRowViewTracking()
        },
        error: (error: any) => {
          console.error('❌ Exclusive Benefits: Error cargando Fila 1:', error);
          this.errorFila1 = true;
          this.isLoadingFila1 = false;
          this.cdr.markForCheck();
        }
      });
  }

  private loadFila2(deviceType: 'xl' | 'desktop' | 'tablet' | 'movil') {
    console.log('🔄 Exclusive Benefits: Cargando Fila 2');
    this.isLoadingFila2 = true;
    this.errorFila2 = false;
    this.cdr.markForCheck();

    this.discountsService.getBenefitsByRowAndDevice('fila2', deviceType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: BenefitItem[]) => {
          console.log('✅ Exclusive Benefits: Fila 2 cargada exitosamente', items.length);
          this.fila2Items = items.sort((a: BenefitItem, b: BenefitItem) => a.orden - b.orden);
          this.isLoadingFila2 = false;
          this.cdr.markForCheck();

          // Los eventos de vista se manejarán en setupRowViewTracking()
        },
        error: (error: any) => {
          console.error('❌ Exclusive Benefits: Error cargando Fila 2:', error);
          this.errorFila2 = true;
          this.isLoadingFila2 = false;
          this.cdr.markForCheck();
        }
      });
  }

  private loadFila3(deviceType: 'xl' | 'desktop' | 'tablet' | 'movil') {
    console.log('🔄 Exclusive Benefits: Cargando Fila 3');
    this.isLoadingFila3 = true;
    this.errorFila3 = false;
    this.cdr.markForCheck();

    this.discountsService.getBenefitsByRowAndDevice('fila3', deviceType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: BenefitItem[]) => {
          console.log('✅ Exclusive Benefits: Fila 3 cargada exitosamente', items.length);
          this.fila3Items = items.sort((a: BenefitItem, b: BenefitItem) => a.orden - b.orden);
          this.isLoadingFila3 = false;
          this.cdr.markForCheck();

          // Los eventos de vista se manejarán en setupRowViewTracking()
        },
        error: (error: any) => {
          console.error('❌ Exclusive Benefits: Error cargando Fila 3:', error);
          this.errorFila3 = true;
          this.isLoadingFila3 = false;
          this.cdr.markForCheck();
        }
      });
  }

  setHoveredCard(cardNumber: number | null) {
    // Usar debounce para evitar llamadas excesivas
    this.hoverSubject$.next(cardNumber);
  }

  onCardClick(item: BenefitItem): void {
    if (item.url && item.url !== '#') {
      console.log('🖱️ Exclusive Benefits: Clic en tarjeta', item.titulo, item.url);

      // Track promotion selection event
      this.trackPromotionSelection(item);

      // Si es una URL externa, abrir en nueva pestaña
      if (item.url.startsWith('http')) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      } else {
        // Si es una ruta interna, usar router
        this.router.navigate([item.url]);
      }
    }
  }

  isCardClickable(item: BenefitItem): boolean {
    return !!(item.url && item.url !== '#');
  }

  getFallbackImage(index: number): string {
    // Usar un placeholder genérico en lugar de imágenes que no existen
    const placeholderImages = [
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlc2N1ZW50byAxPC90ZXh0Pjwvc3ZnPg==',
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTRmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzM3NDE1MSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlc2N1ZW50byAyPC90ZXh0Pjwvc3ZnPg==',
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRmMmY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzM3NDE1MSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlc2N1ZW50byAzPC90ZXh0Pjwvc3ZnPg==',
      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZmVmM2JkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzM3NDE1MSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlc2N1ZW50byA0PC90ZXh0Pjwvc3ZnPg=='
    ];
    return placeholderImages[index] || placeholderImages[0];
  }

  getFallbackAlt(index: number): string {
    const fallbackAlts = [
      'Hasta 40% de descuento en verduras seleccionadas',
      '20% de descuento en carne argentina y uruguaya',
      'Papel higiénico a $2.000',
      'Pack x3 Mantequilla a $1.290'
    ];
    return fallbackAlts[index] || 'Descuento exclusivo';
  }

  trackByDiscountId(index: number, item: BenefitItem): number {
    return item.id;
  }

  onImageError(event: Event, index: number): void {
    const img = event.target as HTMLImageElement;
    console.warn(`⚠️ Exclusive Benefits: Error cargando imagen para descuento ${index + 1}, usando placeholder`);
    img.src = this.getFallbackImage(index);
  }

  // Método para rastrear la selección de promoción
  private trackPromotionSelection(item: BenefitItem): void {
    const eventData = this.analyticsService.createPromotionEventData(
      item.id || 0,
      this.deviceType,
      item.titulo || '',
      item.img || '',
      'exclusive_benefits_card'
    );

    this.analyticsService.trackPromotionSelection(eventData);
  }

  // Método para configurar el tracking de vistas de las filas
  private setupRowViewTracking(): void {
    console.log('🔍 Exclusive Benefits: Configurando tracking de vistas para filas');
    
    // Verificar que todas las filas estén cargadas
    const allFilasLoaded = !this.isLoadingFila1 && !this.isLoadingFila2 && !this.isLoadingFila3;
    
    if (!allFilasLoaded) {
      console.log('⏳ Exclusive Benefits: Esperando a que todas las filas se carguen...');
      setTimeout(() => this.setupRowViewTracking(), 1000);
      return;
    }

    console.log('✅ Exclusive Benefits: Todas las filas cargadas, registrando tarjetas...');
    
    // Solo registrar las filas para tracking, no enviar eventos aún
    this.registerRowsForTracking();
    
    // Debug después de configurar el tracking
    setTimeout(() => {
      this.debugTrackingStatus();
    }, 1000);
  }

  // Método para registrar cada fila individualmente en el sistema de tracking
  private registerRowsForTracking(): void {
    // Registrar Fila 1 - cada tarjeta individual
    if (this.fila1Element?.nativeElement && this.fila1Items.length > 0) {
      console.log('🔍 Registrando Fila 1...');
      const cards = this.fila1Element.nativeElement.querySelectorAll('.exclusive-benefits__card');
      console.log('📊 Fila 1 - Tarjetas DOM encontradas:', cards.length);
      console.log('📊 Fila 1 - Items disponibles:', this.fila1Items.length);
      
      cards.forEach((card: HTMLElement, index: number) => {
        if (this.fila1Items[index]) {
          const item = this.fila1Items[index];
          console.log(`📊 Registrando tarjeta ${index + 1} de Fila 1:`, item.titulo);
          this.analyticsService.registerPromotionForTracking(
            card,
            item.id?.toString() || `fila1-${index}`,
            item.titulo || `Promoción ${index + 1}`,
            item.img || '',
            'exclusive-benefits-fila1'
          );
        }
      });
      console.log('✅ Exclusive Benefits: Fila 1 registrada para tracking -', this.fila1Items.length, 'tarjetas individuales');
    }

    // Registrar Fila 2 - cada tarjeta individual
    if (this.fila2Element?.nativeElement && this.fila2Items.length > 0) {
      console.log('🔍 Registrando Fila 2...');
      const cards = this.fila2Element.nativeElement.querySelectorAll('.exclusive-benefits__card-large');
      console.log('📊 Fila 2 - Tarjetas DOM encontradas:', cards.length);
      console.log('📊 Fila 2 - Items disponibles:', this.fila2Items.length);
      
      cards.forEach((card: HTMLElement, index: number) => {
        if (this.fila2Items[index]) {
          const item = this.fila2Items[index];
          console.log(`📊 Registrando tarjeta ${index + 1} de Fila 2:`, item.titulo);
          this.analyticsService.registerPromotionForTracking(
            card,
            item.id?.toString() || `fila2-${index}`,
            item.titulo || `Promoción ${index + 1}`,
            item.img || '',
            'exclusive-benefits-fila2'
          );
        }
      });
      console.log('✅ Exclusive Benefits: Fila 2 registrada para tracking -', this.fila2Items.length, 'tarjetas individuales');
    }

    // Registrar Fila 3 - cada tarjeta individual
    if (this.fila3Element?.nativeElement && this.fila3Items.length > 0) {
      console.log('🔍 Registrando Fila 3...');
      const cards = this.fila3Element.nativeElement.querySelectorAll('.exclusive-benefits__card');
      console.log('📊 Fila 3 - Tarjetas DOM encontradas:', cards.length);
      console.log('📊 Fila 3 - Items disponibles:', this.fila3Items.length);
      
      cards.forEach((card: HTMLElement, index: number) => {
        if (this.fila3Items[index]) {
          const item = this.fila3Items[index];
          console.log(`📊 Registrando tarjeta ${index + 1} de Fila 3:`, item.titulo);
          this.analyticsService.registerPromotionForTracking(
            card,
            item.id?.toString() || `fila3-${index}`,
            item.titulo || `Promoción ${index + 1}`,
            item.img || '',
            'exclusive-benefits-fila3'
          );
        }
      });
      console.log('✅ Exclusive Benefits: Fila 3 registrada para tracking -', this.fila3Items.length, 'tarjetas individuales');
    }
  }

  // Método para rastrear cuando se muestra una promoción (view) - específico para cada fila
  private trackPromotionViewForRow(item: BenefitItem, fila: string, rowElement: HTMLElement): void {
    const eventData = this.analyticsService.createPromotionEventData(
      item.id || 0,
      this.deviceType,
      item.titulo || '',
      item.img || '',
      `exclusive_benefits_${fila}`
    );

    console.log(`🔍 Exclusive Benefits: Intentando track para ${fila} -`, eventData);

    // Intentar primero con el tracking de visibilidad
    this.analyticsService.trackPromotionViewIfVisible(eventData, rowElement);
    
    // Como fallback, también enviar directamente después de un pequeño delay
    setTimeout(() => {
      console.log(`📊 Exclusive Benefits: Enviando evento directo para ${fila} -`, item.titulo);
      this.analyticsService.trackPromotionView(eventData);
    }, 2000);
  }

  // Método para rastrear cuando se muestra una promoción (view) - solo si la sección es visible (método anterior)
  private trackPromotionViewIfVisible(item: BenefitItem, fila: string): void {
    if (!this.sectionElement?.nativeElement) return;

    const eventData = this.analyticsService.createPromotionEventData(
      item.id || 0,
      this.deviceType,
      item.titulo || '',
      item.img || '',
      `exclusive_benefits_${fila}`
    );

    this.analyticsService.trackPromotionViewIfVisible(eventData, this.sectionElement.nativeElement);
  }

  // Método para rastrear cuando se muestra una promoción (view) - método directo (mantener para compatibilidad)
  private trackPromotionView(item: BenefitItem, fila: string): void {
    const eventData = this.analyticsService.createPromotionEventData(
      item.id || 0,
      this.deviceType,
      item.titulo || '',
      item.img || '',
      `exclusive_benefits_${fila}`
    );

    this.analyticsService.trackPromotionView(eventData);
  }

  // Método para debugging - verificar estado del servicio
  getServiceStatus() {
    return this.discountsService.getCacheStatus();
  }

  // Método para debugging - verificar estado del tracking
  debugTrackingStatus() {
    console.log('🔍 Exclusive Benefits: Estado del tracking');
    console.log('📊 Fila 1 - Items:', this.fila1Items.length, 'Elemento:', !!this.fila1Element?.nativeElement);
    console.log('📊 Fila 2 - Items:', this.fila2Items.length, 'Elemento:', !!this.fila2Element?.nativeElement);
    console.log('📊 Fila 3 - Items:', this.fila3Items.length, 'Elemento:', !!this.fila3Element?.nativeElement);
    console.log('📊 Sección principal:', !!this.sectionElement?.nativeElement);
    
    // Verificar tarjetas individuales registradas
    if (this.fila1Element?.nativeElement) {
      const cards = this.fila1Element.nativeElement.querySelectorAll('.exclusive-benefits__card');
      console.log('📊 Fila 1 - Tarjetas encontradas:', cards.length);
      cards.forEach((card: HTMLElement, index: number) => {
        const promotionId = card.getAttribute('data-promotion-id');
        console.log(`📊 Fila 1 - Tarjeta ${index + 1} promotion-id:`, promotionId);
      });
    }
    if (this.fila2Element?.nativeElement) {
      const cards = this.fila2Element.nativeElement.querySelectorAll('.exclusive-benefits__card-large');
      console.log('📊 Fila 2 - Tarjetas encontradas:', cards.length);
      cards.forEach((card: HTMLElement, index: number) => {
        const promotionId = card.getAttribute('data-promotion-id');
        console.log(`📊 Fila 2 - Tarjeta ${index + 1} promotion-id:`, promotionId);
      });
    }
    if (this.fila3Element?.nativeElement) {
      const cards = this.fila3Element.nativeElement.querySelectorAll('.exclusive-benefits__card');
      console.log('📊 Fila 3 - Tarjetas encontradas:', cards.length);
      cards.forEach((card: HTMLElement, index: number) => {
        const promotionId = card.getAttribute('data-promotion-id');
        console.log(`📊 Fila 3 - Tarjeta ${index + 1} promotion-id:`, promotionId);
      });
    }
  }

  // Métodos para verificar si mostrar cada fila
  shouldShowFila1(): boolean {
    return !this.isLoadingFila1 && !this.errorFila1 && this.fila1Items.length > 0;
  }

  shouldShowFila2(): boolean {
    return !this.isLoadingFila2 && !this.errorFila2 && this.fila2Items.length > 0;
  }

  shouldShowFila3(): boolean {
    return !this.isLoadingFila3 && !this.errorFila3 && this.fila3Items.length > 0;
  }

  // Método para obtener el total de items cargados
  getTotalItems(): number {
    return this.fila1Items.length + this.fila2Items.length + this.fila3Items.length;
  }

  // Métodos para obtener todas las tarjetas (sin filtrar por dispositivo)
  getFilteredFila1Items(): BenefitItem[] {
    // Mostrar todas las tarjetas independientemente del dispositivo
    return this.fila1Items;
  }

  getFilteredFila2Items(): BenefitItem[] {
    // Mostrar todas las tarjetas independientemente del dispositivo
    return this.fila2Items;
  }

  getFilteredFila3Items(): BenefitItem[] {
    // Mostrar todas las tarjetas independientemente del dispositivo
    return this.fila3Items;
  }

  // Método para detectar si estamos en pantalla móvil
  private isMobileScreen(): boolean {
    return window.innerWidth <= 768;
  }

  // Método para obtener el ancho actual de la ventana (para debugging)
  getCurrentWidth(): number {
    return window.innerWidth;
  }
}
